import type { ApiResponse, CourtHireRate } from '@court-sync/shared';
import { apiGet, apiPost } from './apiClient';

export interface MemberCourtHireBookingRow {
  id: string;
  courtId: string;
  courtName: string | null;
  startAt: string;
  endAt: string;
  priceCents: number;
  currency: string;
  status: string;
  hasReceipt: boolean;
}

export interface MemberInventoryHireRow {
  id: string;
  itemId: string;
  itemName: string;
  quantity: number;
  startAt: string;
  endAt: string;
  status: string;
  courtHireBookingId: string | null;
}

export interface MemberNoticeRow {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
  isRead: boolean;
}

async function memberGet<T>(clubSlug: string, path: string): Promise<ApiResponse<T>> {
  return apiGet<T>(`/public/club-membership/${clubSlug}${path}`);
}

export interface MemberCourtHireBookingResult {
  bookingId: string;
  paymentRequired?: boolean;
  status?: 'pending' | 'confirmed';
  clientSecret?: string;
  paymentIntentId?: string;
  stripeConnectAccountId?: string;
  subtotalCents?: number;
  taxAmountCents?: number;
  taxInclusive?: boolean;
  totalPriceCents?: number;
  discountPercent?: number;
}

async function memberPost<T>(clubSlug: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
  return apiPost<T>(`/public/club-membership/${clubSlug}${path}`, body ?? {});
}

export const memberPortalService = {
  async getCourtHireRates(clubSlug: string): Promise<ApiResponse<CourtHireRate[]>> {
    return memberGet<CourtHireRate[]>(clubSlug, '/court-hire/rates');
  },

  async createCourtHireBooking(
    clubSlug: string,
    payload: {
      courtId: string;
      startAt: string;
      endAt: string;
      rateId?: string;
      addons?: Array<{ itemId: string; quantity: number }>;
    }
  ): Promise<ApiResponse<MemberCourtHireBookingResult>> {
    return memberPost<MemberCourtHireBookingResult>(clubSlug, '/court-hire/bookings', payload);
  },

  async getMyCourtHireBookings(clubSlug: string): Promise<ApiResponse<MemberCourtHireBookingRow[]>> {
    return memberGet<MemberCourtHireBookingRow[]>(clubSlug, '/my-court-hire-bookings');
  },

  async cancelCourtHireBooking(
    clubSlug: string,
    bookingId: string
  ): Promise<ApiResponse<null>> {
    return memberPost<null>(clubSlug, `/court-hire/bookings/${bookingId}/cancel`);
  },

  async getCourtHireBookingReceipt(
    clubSlug: string,
    bookingId: string
  ): Promise<ApiResponse<{ receiptUrl: string }>> {
    return memberGet<{ receiptUrl: string }>(
      clubSlug,
      `/court-hire/bookings/${bookingId}/receipt`
    );
  },

  async cancelPendingCourtHireBookings(
    clubSlug: string
  ): Promise<
    ApiResponse<{
      cancelled: number;
      cancelledCourtBookings: number;
      cancelledEquipmentHires: number;
    }>
  > {
    return memberPost<{
      cancelled: number;
      cancelledCourtBookings: number;
      cancelledEquipmentHires: number;
    }>(clubSlug, '/court-hire/bookings/cancel-pending');
  },

  async getMyInventoryHires(clubSlug: string): Promise<ApiResponse<MemberInventoryHireRow[]>> {
    return memberGet<MemberInventoryHireRow[]>(clubSlug, '/my-inventory-hires');
  },

  async getNotices(clubSlug: string): Promise<ApiResponse<MemberNoticeRow[]>> {
    return memberGet<MemberNoticeRow[]>(clubSlug, '/notices');
  },

  async markNoticeRead(clubSlug: string, noticeId: string): Promise<ApiResponse<null>> {
    return memberPost<null>(clubSlug, `/notices/${noticeId}/read`);
  },
};
